import React, { useState, useEffect } from 'react';
import { Alert, Button, Modal, Badge, Card, Row, Col } from 'react-bootstrap';
import { Bell, X, ExclamationTriangleFill, InfoCircleFill, CheckCircleFill, BellFill } from 'react-bootstrap-icons';
import { useAuth } from '../context/AuthContext';
import systemAlertService from '../services/systemAlertService';

/**
 * 통합 시스템 알림 표시 컴포넌트
 * IA-08 요구사항에 따른 시스템 사용 알림 메시지를 표시
 * 로그인 후 사용자에게 시스템 알림을 표시하고 확인 처리 기능 제공
 */
const SystemAlertDisplay = ({ 
    displayMode = 'floating', // 'floating', 'inline', 'modal'
    maxInlineAlerts = 3,
    onAcknowledge 
}) => {
  const { systemAlerts, isAuthenticated } = useAuth();
  const [visibleAlerts, setVisibleAlerts] = useState([]);
  const [acknowledgedAlerts, setAcknowledgedAlerts] = useState(new Set());
  const [showModal, setShowModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);

  // 우선순위를 숫자로 변환 (낮을수록 높은 우선순위)
  const getPriorityValue = (priority) => {
    const priorityStr = String(priority ?? "").toLowerCase();
    switch (priorityStr) {
      case "high":
      case "1":
        return 1;
      case "medium":
      case "2":
        return 2;
      case "low":
      case "3":
        return 3;
      default:
        return 99;
    }
  };

  useEffect(() => {
    if (isAuthenticated && systemAlerts.length > 0) {
      // 활성화된 알림만 필터링 (확인되지 않은 알림)
      const activeAlerts = systemAlerts.filter(
        (alert) => alert.isActive !== false && !acknowledgedAlerts.has(alert.id)
      );
      // 우선순위 순으로 정렬 (높은 우선순위가 위에 표시)
      const sortedAlerts = [...activeAlerts].sort(
        (a, b) => getPriorityValue(a.priority) - getPriorityValue(b.priority)
      );
      setVisibleAlerts(sortedAlerts);
    } else {
      setVisibleAlerts([]);
    }
  }, [systemAlerts, isAuthenticated, acknowledgedAlerts]);

  const getPriorityVariant = (priority) => {
    // 숫자 또는 문자열 모두 처리
    const priorityStr = String(priority ?? "").toLowerCase();
    switch (priorityStr) {
      case "high":
      case "1": // 숫자 우선순위 (1 = 높음)
        return "danger";
      case "medium":
      case "2": // 숫자 우선순위 (2 = 보통)
        return "warning";
      case "low":
      case "3": // 숫자 우선순위 (3 = 낮음)
        return "info";
      default:
        return "secondary";
    }
  };

  const getPriorityLabel = (priority) => {
    const priorityStr = String(priority ?? "").toLowerCase();
    switch (priorityStr) {
      case "high":
      case "1":
        return "높음";
      case "medium":
      case "2":
        return "보통";
      case "low":
      case "3":
        return "낮음";
      default:
        return "보통";
    }
  };

  const getAlertIcon = (alertType) => {
    switch (alertType?.toLowerCase()) {
      case "security":
        return <ExclamationTriangleFill className="me-2" />;
      case "maintenance":
        return <InfoCircleFill className="me-2" />;
      case "update":
        return <CheckCircleFill className="me-2" />;
      default:
        return <Bell className="me-2" />;
    }
  };

  // 알림 확인 처리
  const handleAcknowledge = async (alertId, note = "") => {
    try {
      if (onAcknowledge) {
        await onAcknowledge(alertId, note);
      } else {
        // 기본 확인 처리 - systemAlertService 사용
        await systemAlertService.acknowledgeAlert(alertId, "admin");
      }
      setAcknowledgedAlerts((prev) => new Set([...prev, alertId]));
    } catch (error) {
      console.error("알림 확인 처리 실패:", error);
    }
  };

  const dismissAlert = (alertId) => {
    handleAcknowledge(alertId);
  };

  const showAlertDetail = (alert) => {
    setSelectedAlert(alert);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedAlert(null);
  };

  if (!isAuthenticated || visibleAlerts.length === 0) {
    return null;
  }

  // 표시 모드에 따른 렌더링
  const renderFloatingAlerts = () => (
    <div
      className="system-alerts-container"
      style={{
        position: "fixed",
        top: "80px", // 상단 메뉴(약 60px) 아래로 위치
        right: "20px",
        zIndex: 1050,
        maxWidth: "400px",
      }}
    >
      {visibleAlerts.slice(0, 3).map((alert) => (
        <Alert
          key={alert.id}
          variant={getPriorityVariant(alert.priority)}
          dismissible
          onClose={() => dismissAlert(alert.id)}
          className="mb-2 shadow-sm"
        >
          <div className="d-flex align-items-start">
            <div className="flex-shrink-0">
              {getAlertIcon(alert.alertType || alert.alert_type)}
            </div>
            <div className="flex-grow-1">
              <Alert.Heading as="h6" className="mb-1">
                {alert.title}
                <Badge
                  bg={getPriorityVariant(alert.priority)}
                  className="ms-2 small"
                >
                  {getPriorityLabel(alert.priority)}
                </Badge>
              </Alert.Heading>
              <div className="small mb-2">
                {alert.message.length > 100
                  ? `${alert.message.substring(0, 100)}...`
                  : alert.message}
              </div>
              <div className="d-flex justify-content-between align-items-center">
                {alert.message.length > 100 && (
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-decoration-none"
                    onClick={() => showAlertDetail(alert)}
                  >
                    자세히 보기
                  </Button>
                )}
                <Button
                  variant="outline-success"
                  size="sm"
                  onClick={() => handleAcknowledge(alert.id)}
                >
                  확인
                </Button>
              </div>
            </div>
          </div>
        </Alert>
      ))}
      {visibleAlerts.length > 3 && (
        <Alert variant="light" className="text-center mb-2 shadow-sm">
          <small>
            {visibleAlerts.length - 3}개의 추가 알림이 있습니다.
            <Button
              variant="link"
              size="sm"
              onClick={() => setSelectedAlert("list")}
              className="p-0 ms-2"
            >
              모든 알림 보기
            </Button>
          </small>
        </Alert>
      )}
    </div>
  );

  const renderInlineAlerts = () => (
    <div className="system-alerts-container mb-3">
      {visibleAlerts.slice(0, maxInlineAlerts).map((alert) => (
        <Alert
          key={alert.id}
          variant={getPriorityVariant(alert.priority)}
          dismissible
          onClose={() => dismissAlert(alert.id)}
          className="d-flex align-items-center justify-content-between"
        >
          <div className="d-flex align-items-center">
            {getAlertIcon(alert.alertType || alert.alert_type)}
            <div>
              <strong>{alert.title}</strong>
              <div className="small">{alert.message}</div>
            </div>
          </div>
          <div className="d-flex align-items-center">
            <Badge bg={getPriorityVariant(alert.priority)} className="me-2">
              {getPriorityLabel(alert.priority)}
            </Badge>
            <Button
              variant="outline-secondary"
              size="sm"
              className="me-1"
              onClick={() => showAlertDetail(alert)}
            >
              상세보기
            </Button>
            <Button
              variant="outline-success"
              size="sm"
              onClick={() => handleAcknowledge(alert.id)}
            >
              확인
            </Button>
          </div>
        </Alert>
      ))}

      {visibleAlerts.length > maxInlineAlerts && (
        <Alert variant="light" className="text-center">
          <small>
            {visibleAlerts.length - maxInlineAlerts}개의 추가 알림이 있습니다.
            <Button
              variant="link"
              size="sm"
              onClick={() => setSelectedAlert("list")}
            >
              모든 알림 보기
            </Button>
          </small>
        </Alert>
      )}
    </div>
  );

  return (
    <>
      {/* 표시 모드에 따른 렌더링 */}
      {displayMode === "floating" && renderFloatingAlerts()}
      {displayMode === "inline" && renderInlineAlerts()}
      {displayMode === "modal" && (
        <Modal show={true} onHide={closeModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <BellFill className="me-2" />
              시스템 알림 ({visibleAlerts.length})
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {visibleAlerts.map((alert) => (
              <Card key={alert.id} className="mb-3">
                <Card.Header
                  className={`bg-${getPriorityVariant(
                    alert.priority
                  )} text-white`}
                >
                  <Row>
                    <Col>
                      <div className="d-flex align-items-center">
                        {getAlertIcon(alert.alertType || alert.alert_type)}
                        <strong>{alert.title}</strong>
                      </div>
                    </Col>
                    <Col xs="auto">
                      <Badge bg="light" text="dark">
                        {alert.priority?.toUpperCase()}
                      </Badge>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body>
                  <p>{alert.message}</p>
                  {alert.display_start_date && (
                    <small className="text-muted">
                      표시 시작:{" "}
                      {new Date(alert.display_start_date).toLocaleString()}
                    </small>
                  )}
                  {alert.display_end_date && (
                    <small className="text-muted ms-3">
                      표시 종료:{" "}
                      {new Date(alert.display_end_date).toLocaleString()}
                    </small>
                  )}
                </Card.Body>
                <Card.Footer>
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => handleAcknowledge(alert.id)}
                  >
                    확인
                  </Button>
                </Card.Footer>
              </Card>
            ))}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeModal}>
              닫기
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                visibleAlerts.forEach((alert) => handleAcknowledge(alert.id));
                closeModal();
              }}
            >
              모든 알림 확인
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* 알림 상세 보기 모달 */}
      {selectedAlert && selectedAlert !== "list" && (
        <Modal show={true} onHide={() => setSelectedAlert(null)}>
          <Modal.Header closeButton>
            <Modal.Title className="d-flex align-items-center">
              {getAlertIcon(
                selectedAlert.alertType || selectedAlert.alert_type
              )}
              {selectedAlert.title}
              <Badge
                bg={getPriorityVariant(selectedAlert.priority)}
                className="ms-2"
              >
                {getPriorityLabel(selectedAlert.priority)}
              </Badge>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-3">
              <strong>알림 타입:</strong>{" "}
              <Badge bg="secondary">
                {(selectedAlert.alertType || selectedAlert.alert_type) ===
                "security"
                  ? "보안"
                  : (selectedAlert.alertType || selectedAlert.alert_type) ===
                    "maintenance"
                  ? "유지보수"
                  : (selectedAlert.alertType || selectedAlert.alert_type) ===
                    "update"
                  ? "업데이트"
                  : "일반"}
              </Badge>
            </div>
            <div className="mb-3">
              <strong>메시지:</strong>
              <div className="mt-2 p-3 bg-light rounded">
                {selectedAlert.message}
              </div>
            </div>
            {(selectedAlert.display_start_date ||
              selectedAlert.display_end_date) && (
              <div className="mb-3">
                <strong>표시 기간:</strong>
                <div className="mt-1 small text-muted">
                  {selectedAlert.display_start_date && (
                    <div>
                      시작:{" "}
                      {new Date(
                        selectedAlert.display_start_date
                      ).toLocaleString()}
                    </div>
                  )}
                  {selectedAlert.display_end_date && (
                    <div>
                      종료:{" "}
                      {new Date(
                        selectedAlert.display_end_date
                      ).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setSelectedAlert(null)}>
              닫기
            </Button>
            <Button
              variant="success"
              onClick={() => {
                handleAcknowledge(selectedAlert.id);
                setSelectedAlert(null);
              }}
            >
              확인
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* 모든 알림 목록 모달 */}
      {selectedAlert === "list" && (
        <Modal show={true} onHide={() => setSelectedAlert(null)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <BellFill className="me-2" />
              모든 시스템 알림 ({visibleAlerts.length})
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
            {visibleAlerts.map((alert) => (
              <div key={alert.id} className="border-bottom pb-2 mb-2">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-1">
                      {getAlertIcon(alert.alertType || alert.alert_type)}
                      <strong>{alert.title}</strong>
                      <Badge
                        bg={getPriorityVariant(alert.priority)}
                        className="ms-2"
                      >
                        {getPriorityLabel(alert.priority)}
                      </Badge>
                    </div>
                    <p className="mb-1 small">{alert.message}</p>
                    {alert.display_start_date && (
                      <small className="text-muted">
                        {new Date(
                          alert.display_start_date
                        ).toLocaleDateString()}
                      </small>
                    )}
                  </div>
                  <div className="d-flex flex-column">
                    <Button
                      variant="outline-info"
                      size="sm"
                      className="mb-1"
                      onClick={() => setSelectedAlert(alert)}
                    >
                      상세보기
                    </Button>
                    <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => handleAcknowledge(alert.id)}
                    >
                      확인
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setSelectedAlert(null)}>
              닫기
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                visibleAlerts.forEach((alert) => handleAcknowledge(alert.id));
                setSelectedAlert(null);
              }}
            >
              모든 알림 확인
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default SystemAlertDisplay;
